import { useState, useEffect } from 'react';
import logger from '../utils/logger';

export interface WalletAddresses {
    [key: string]: string;
}

interface UseWalletAddressesReturn {
    addresses: WalletAddresses;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

export const useWalletAddresses = (chainId?: string): UseWalletAddressesReturn => {
    const [addresses, setAddresses] = useState<WalletAddresses>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAddresses = async () => {
        if (!chainId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(
                `/api/wallet-addresses?chainId=${chainId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.error || `HTTP error! status: ${response.status}`
                );
            }

            const data = await response.json();

            if (!data.success || !data.addresses) {
                throw new Error('Invalid response format from server');
            }

            logger.log('Wallet addresses fetched successfully for chain:', chainId);
            setAddresses(data.addresses);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            logger.error('Error fetching wallet addresses:', errorMessage);
            setError(errorMessage);
            setAddresses({}); // Reset addresses on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, [chainId]);

    return {
        addresses,
        loading,
        error,
        refetch: fetchAddresses
    };
};
